import { useState, useEffect } from 'react';
import {
  Grid, Card, CardContent, Typography, Box, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
  Alert, AlertTitle, Chip, LinearProgress, MenuItem, Select,
  FormControl, InputLabel, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Snackbar,
  IconButton, Tooltip, CircularProgress
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MessageIcon from '@mui/icons-material/Message';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SmsIcon from '@mui/icons-material/Sms';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HistoryIcon from '@mui/icons-material/History';
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  GetSmsWalletSummary,
  GetSmsPackages,
  AddSmsPackage,
  GetSmsSentHistory,
  UpdateAlertThreshold,
} from '../../services/SmsWalletService';

// ─── Status chip ──────────────────────────────────────────────────────────────
// Sent → green | Queued → warning (orange) | Failed → red | fallback → default
const StatusChip = ({ status, deliveryMessage }) => {
  const s = (status || '').toLowerCase();
  const color = s === 'sent' ? 'success' : s === 'queued' ? 'warning' : s === 'failed' ? 'error' : 'default';
  const label = status || 'Unknown';
  return (
    <Tooltip title={deliveryMessage || label} placement="top" arrow>
      <Chip label={label} color={color} size="small" />
    </Tooltip>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ title, value, subtitle, icon, color, bgColor }) => (
  <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600} mb={0.5}>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>
            {value ?? <CircularProgress size={28} />}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: bgColor || '#e3f2fd', borderRadius: 2, p: 1.2, display: 'flex', alignItems: 'center' }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Package options ──────────────────────────────────────────────────────────
const PACKAGE_OPTIONS = [
  { label: '1,000 SMS',  value: 1000 },
  { label: '2,000 SMS',  value: 2000 },
  { label: '5,000 SMS',  value: 5000 },
  { label: '10,000 SMS', value: 10000 },
  { label: '25,000 SMS', value: 25000 },
  { label: 'Custom',     value: 'custom' },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const SMSWalletModule = () => {
  const [summary,        setSummary]        = useState(null);
  const [packages,       setPackages]       = useState([]);
  const [history,        setHistory]        = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Add package dialog
  const [openAddPkg,  setOpenAddPkg]  = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(5000);
  const [customSms,   setCustomSms]   = useState('');
  const [pkgNote,     setPkgNote]     = useState('');
  const [addingPkg,   setAddingPkg]   = useState(false);

  // Alert settings dialog
  const [openAlertSettings, setOpenAlertSettings] = useState(false);
  const [threshold,          setThreshold]         = useState('');
  const [savingThreshold,    setSavingThreshold]   = useState(false);

  // History filters + pagination
  const [historyPage,        setHistoryPage]        = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [filterDate,         setFilterDate]         = useState('');

  // Snackbar
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  // ── Load data ─────────────────────────────────────────────────────────────
  const loadSummary = () => {
    GetSmsWalletSummary(
      (res) => { setSummary(res?.data || mockSummary); setThreshold(res?.data?.alertThreshold ?? 500); },
      ()    => { setSummary(mockSummary); setThreshold(mockSummary.alertThreshold); }
    );
  };

  const loadPackages = () => {
    GetSmsPackages(
      (res) => setPackages(res?.data || mockPackages),
      ()    => setPackages(mockPackages)
    );
  };

  const loadHistory = () => {
    setLoadingHistory(true);
    GetSmsSentHistory(
      { date: filterDate },
      (res) => { setHistory(res?.data || mockHistory); setLoadingHistory(false); },
      ()    => { setHistory(mockHistory);               setLoadingHistory(false); }
    );
  };

  useEffect(() => { loadSummary(); loadPackages(); loadHistory(); }, []); // eslint-disable-line
  useEffect(() => { loadHistory(); setHistoryPage(0); }, [filterDate]);   // eslint-disable-line

  // ── Add package ───────────────────────────────────────────────────────────
  const handleAddPackage = () => {
    const smsCount = selectedPkg === 'custom' ? parseInt(customSms) : selectedPkg;
    if (!smsCount || smsCount < 1) { showSnack('Please enter a valid SMS count', 'error'); return; }
    setAddingPkg(true);
    AddSmsPackage(
      { smsCount, note: pkgNote },
      () => {
        showSnack(`${smsCount.toLocaleString()} SMS added successfully!`);
        setOpenAddPkg(false); setCustomSms(''); setPkgNote(''); setSelectedPkg(5000);
        setAddingPkg(false);
        loadSummary(); loadPackages();
      },
      () => { showSnack('Failed to add package. Please try again.', 'error'); setAddingPkg(false); }
    );
  };

  // ── Save threshold ────────────────────────────────────────────────────────
  const handleSaveThreshold = () => {
    if (!threshold || parseInt(threshold) < 1) { showSnack('Enter a valid threshold value', 'error'); return; }
    setSavingThreshold(true);
    UpdateAlertThreshold(
      { threshold: parseInt(threshold) },
      () => { showSnack('Alert threshold updated!'); setOpenAlertSettings(false); setSavingThreshold(false); loadSummary(); },
      () => { showSnack('Failed to update threshold.', 'error'); setSavingThreshold(false); }
    );
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const remainingPct   = summary ? Math.max(0, Math.round((summary.remaining / summary.total) * 100)) : 0;
  const isLowBalance   = summary && summary.remaining <= summary.alertThreshold;
  const isExhausted    = summary && summary.remaining <= 0;
  const progressColor  = isExhausted ? 'error' : isLowBalance ? 'warning' : 'success';
  const historySlice   = history.slice(historyPage * historyRowsPerPage, historyPage * historyRowsPerPage + historyRowsPerPage);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page Header ── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h5" fontWeight={700}>SMS Wallet Module</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage SMS packages · track attendance alerts sent to parents
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Tooltip title="Refresh">
            <IconButton onClick={() => { loadSummary(); loadPackages(); loadHistory(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setOpenAlertSettings(true)}>
            Alert Settings
          </Button>
          <Button variant="contained" startIcon={<AddCircleOutlineIcon />} onClick={() => setOpenAddPkg(true)}>
            Add SMS Package
          </Button>
        </Box>
      </Box>

      {/* ── Low Balance / Exhausted Alert ── */}
      {isExhausted && (
        <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mb: 2 }} variant="filled">
          <AlertTitle>SMS Balance Exhausted!</AlertTitle>
          Your SMS balance is <strong>0</strong>. Attendance alerts cannot be sent to parents.
          Please add an SMS package immediately.
        </Alert>
      )}
      {!isExhausted && isLowBalance && (
        <Alert severity="warning" icon={<NotificationsActiveIcon />} sx={{ mb: 2 }}>
          <AlertTitle>Low SMS Balance Warning</AlertTitle>
          Only <strong>{summary?.remaining?.toLocaleString()}</strong> SMS remaining.
          Threshold is <strong>{summary?.alertThreshold?.toLocaleString()}</strong>. Please recharge.
        </Alert>
      )}

      {/* ── Stat Cards ── */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total SMS Purchased" value={summary?.total?.toLocaleString()} subtitle="All time"
            icon={<ShoppingCartIcon sx={{ color: '#1565c0' }} />} bgColor="#e3f2fd" color="#1565c0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="SMS Remaining" value={summary?.remaining?.toLocaleString()} subtitle={`${remainingPct}% left`}
            icon={<SmsIcon sx={{ color: isLowBalance ? '#e65100' : '#2e7d32' }} />}
            bgColor={isLowBalance ? '#fff3e0' : '#e8f5e9'} color={isLowBalance ? '#e65100' : '#2e7d32'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Attendance SMS Sent" value={summary?.used?.toLocaleString()} subtitle="Total sent to parents"
            icon={<MessageIcon sx={{ color: '#6a1b9a' }} />} bgColor="#f3e5f5" color="#6a1b9a" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="SMS Sent Today" value={summary?.sentToday?.toLocaleString()}
            subtitle={new Date().toLocaleDateString('en-IN')}
            icon={<CheckCircleIcon sx={{ color: '#00695c' }} />} bgColor="#e0f2f1" color="#00695c" />
        </Grid>
      </Grid>

      {/* ── Balance Progress Bar ── */}
      <Card elevation={2} sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>SMS Balance Usage</Typography>
            <Chip
              label={isExhausted ? 'Exhausted' : isLowBalance ? 'Low Balance' : 'Healthy'}
              color={progressColor} size="small"
            />
          </Box>
          <LinearProgress variant="determinate" value={remainingPct} color={progressColor}
            sx={{ height: 12, borderRadius: 6 }} />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary">
              Used: {summary?.used?.toLocaleString() ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Remaining: {summary?.remaining?.toLocaleString() ?? 0} / {summary?.total?.toLocaleString() ?? 0}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* ── Package Purchase History ── */}
      <Card elevation={2} sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            <ShoppingCartIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            SMS Package Purchase History
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  {['#', 'SMS Count', 'Added On', 'Added By', 'Note', 'Status'].map((h) => (
                    <TableCell key={h}><strong>{h}</strong></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {packages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No packages added yet
                    </TableCell>
                  </TableRow>
                ) : (
                  packages.map((pkg, i) => (
                    <TableRow key={pkg.id || i} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <Typography fontWeight={600} color="primary">{pkg.smsCount?.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>{pkg.addedOn}</TableCell>
                      <TableCell>{pkg.addedBy}</TableCell>
                      <TableCell>{pkg.note || '—'}</TableCell>
                      <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ── Attendance SMS Sent History ── */}
      <Card elevation={2} sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              <HistoryIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
              Attendance SMS Sent to Parents
            </Typography>
            <TextField
              type="date" size="small" label="Filter by Date"
              InputLabelProps={{ shrink: true }} value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              sx={{ width: 180 }}
            />
          </Box>

          {loadingHistory ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ bgcolor: '#f5f5f5' }}><strong>#</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 130 }}><strong>Student Name</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 110 }}><strong>USN / Roll No</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 115 }}><strong>Parent Mobile</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 200 }}><strong>SMS Text</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 150 }}><strong>Sent On</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 130 }}><strong>Gateway Response</strong></TableCell>
                      <TableCell sx={{ bgcolor: '#f5f5f5', minWidth: 90 }}><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historySlice.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          No SMS records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      historySlice.map((row, i) => (
                        <TableRow key={row.id || i} hover>
                          <TableCell>{historyPage * historyRowsPerPage + i + 1}</TableCell>
                          <TableCell>{row.studentName}</TableCell>
                          <TableCell>{row.rollNo}</TableCell>
                          <TableCell>{row.parentMobile}</TableCell>

                          {/* SMS text — truncated with tooltip */}
                          <TableCell sx={{ maxWidth: 220 }}>
                            <Tooltip title={row.smsText || '—'} placement="top" arrow>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}
                              >
                                {row.smsText || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.sentOn}</TableCell>

                          {/* Gateway message: "Messages Queued", "Insufficient funds", etc. */}
                          <TableCell>
                            <Typography
                              variant="caption"
                              sx={{
                                color: row.deliveryMessage === 'Insufficient funds' ? 'error.main'
                                     : row.deliveryMessage === 'Messages Queued'    ? 'warning.main'
                                     : 'text.secondary',
                                fontWeight: row.deliveryMessage === 'Insufficient funds' ? 600 : 400,
                              }}
                            >
                              {row.deliveryMessage || '—'}
                            </Typography>
                          </TableCell>

                          {/* Status chip with tooltip showing delivery message */}
                          <TableCell>
                            <StatusChip status={row.status} deliveryMessage={row.deliveryMessage} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={history.length}
                page={historyPage}
                onPageChange={(_, p) => setHistoryPage(p)}
                rowsPerPage={historyRowsPerPage}
                onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value)); setHistoryPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Add Package Dialog ── */}
      <Dialog open={openAddPkg} onClose={() => setOpenAddPkg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <AddCircleOutlineIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
          Add SMS Package
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Select an SMS package to add to your wallet. Balance is available immediately after confirmation.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Package</InputLabel>
            <Select value={selectedPkg} label="Select Package"
              onChange={(e) => { setSelectedPkg(e.target.value); setCustomSms(''); }}>
              {PACKAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedPkg === 'custom' && (
            <TextField fullWidth label="Enter SMS Count" type="number" value={customSms}
              onChange={(e) => setCustomSms(e.target.value)} inputProps={{ min: 1 }} sx={{ mb: 2 }} />
          )}
          <TextField fullWidth label="Note (optional)" placeholder="e.g. Monthly recharge, Invoice #1234"
            value={pkgNote} onChange={(e) => setPkgNote(e.target.value)} />
          {summary && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current balance: <strong>{summary.remaining?.toLocaleString()}</strong> SMS &nbsp;|&nbsp; After adding:{' '}
              <strong>
                {(
                  (summary.remaining || 0) +
                  (selectedPkg === 'custom' ? parseInt(customSms || 0) : selectedPkg)
                ).toLocaleString()}
              </strong>{' '}SMS
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenAddPkg(false)} disabled={addingPkg}>Cancel</Button>
          <Button variant="contained" onClick={handleAddPackage} disabled={addingPkg}
            startIcon={addingPkg ? <CircularProgress size={16} /> : <AddCircleOutlineIcon />}>
            {addingPkg ? 'Adding...' : 'Add Package'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Alert Settings Dialog ── */}
      <Dialog open={openAlertSettings} onClose={() => setOpenAlertSettings(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <NotificationsActiveIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'warning.main' }} />
          Low Balance Alert Settings
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Set the SMS count at which a low-balance warning is triggered.
          </Typography>
          <TextField fullWidth label="Alert Threshold (SMS Count)" type="number" value={threshold}
            onChange={(e) => setThreshold(e.target.value)} inputProps={{ min: 1 }}
            helperText="Warning shows when remaining SMS falls below this number" />
          {threshold && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Alert triggers when SMS drops below <strong>{parseInt(threshold || 0).toLocaleString()}</strong>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenAlertSettings(false)} disabled={savingThreshold}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleSaveThreshold} disabled={savingThreshold}
            startIcon={savingThreshold ? <CircularProgress size={16} /> : <SettingsIcon />}>
            {savingThreshold ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity} variant="filled" sx={{ minWidth: 280 }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Mock / Demo fallback data ────────────────────────────────────────────────
const mockSummary = {
  total: 10000, used: 6300, remaining: 3700, sentToday: 48, alertThreshold: 500,
};

const mockPackages = [
  { id: 1, smsCount: 5000, addedOn: '10-01-2026 09:00', addedBy: 'Admin', note: 'Initial recharge' },
  { id: 2, smsCount: 5000, addedOn: '15-02-2026 10:30', addedBy: 'Admin', note: 'Monthly recharge' },
];

const mockHistory = Array.from({ length: 35 }, (_, i) => ({
  id: i + 1,
  studentName: `Student ${i + 1}`,
  rollNo: `SH2024${String(i + 1).padStart(3, '0')}`,
  parentMobile: `98765${String(43210 + i).padStart(5, '0')}`,
  smsText: `Dear Parent, your ward was absent on ${new Date(Date.now() - i * 86400000).toLocaleDateString('en-IN')}. - Sahyadri Hostel`,
  sentOn: new Date(Date.now() - i * 3600000).toLocaleString('en-IN'),
  status:          i % 10 === 7 ? 'Failed'         : i % 10 === 3 ? 'Queued' : 'Sent',
  deliveryMessage: i % 10 === 7 ? 'Insufficient funds' : i % 10 === 3 ? 'Messages Queued' : 'Messages Queued',
}));

export default SMSWalletModule;
